import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Sparkles,
  Home,
  Trash2,
  Copy,
  Check,
  Wallet,
  Pencil,
  RotateCcw,
  AlertCircle,
} from 'lucide-react';
import {
  api,
  ApiError,
  type GenerationDto,
  type SessionDetailDto,
  type SessionGetResponse,
  type SessionRewriteResponse,
} from '../lib/api';
import { QuestionStep } from '../components/QuestionStep';
import { StaleSession } from '../components/StaleSession';
import { Markdown } from '../components/ui';
import { clearDraft, filterAnswersInRange, loadDraft, saveDraft } from '../lib/sessionStorage';
import { RewriteModal } from '../components/RewriteModal';
import { OWN_TEXT_IDEA_CODE, type IdeaQuestion } from '@masha/shared';
import {
  saveFeedback as saveRewriteFeedback,
  loadFeedback as loadRewriteFeedback,
  clearFeedback as clearRewriteFeedback,
} from '../lib/rewriteFeedbackStorage';
import { Button, Card, Spinner, Toast } from '../components/ui';
import { haptic } from '../lib/tma';
import { canGoBack } from '../lib/history';
import { StyleStep } from '../components/carousel/StyleStep';
import { CarouselResultView } from '../components/carousel/CarouselResultView';
import { notifyTurboCap } from '../lib/turboNotice';
import { paidRewriteLabel, usePricing } from '../lib/pricing';
import { OwnTextSession } from '../components/carousel/OwnTextSession';

const SAVE_DEBOUNCE_MS = 200;

type ProfileKey = 'niche' | 'product' | 'target_audience';

type Step =
  | { kind: 'profile'; key: ProfileKey; question: IdeaQuestion }
  | { kind: 'idea'; key: `a_${number}`; question: IdeaQuestion };

const PROFILE_QUESTION: Record<ProfileKey, IdeaQuestion> = {
  niche: { title: 'Подтверди свою нишу', description: null },
  product: { title: 'Подтверди свой продукт', description: null },
  target_audience: { title: 'Подтверди свою ЦА', description: null },
};

function buildSteps(
  questions: IdeaQuestion[],
  flags: { niche: boolean; product: boolean; target_audience: boolean },
): Step[] {
  const steps: Step[] = [];
  if (flags.niche) steps.push({ kind: 'profile', key: 'niche', question: PROFILE_QUESTION.niche });
  if (flags.product)
    steps.push({ kind: 'profile', key: 'product', question: PROFILE_QUESTION.product });
  if (flags.target_audience)
    steps.push({
      kind: 'profile',
      key: 'target_audience',
      question: PROFILE_QUESTION.target_audience,
    });
  for (let i = 0; i < questions.length; i++) {
    steps.push({ kind: 'idea', key: `a_${i + 1}`, question: questions[i]! });
  }
  return steps;
}

function buildPayloadFromAnswers(
  next: Record<string, string>,
  currentSteps: Step[],
): Record<string, string> {
  const allowed = new Set<string>(currentSteps.map((s) => s.key));
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(next)) {
    if (!allowed.has(k)) continue;
    const trimmed = v.trim();
    if (trimmed.length === 0) continue;
    out[k] = trimmed;
  }
  return out;
}

export function SessionPage() {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const id = Number(params.id);
  const validId = Number.isInteger(id) && id > 0;

  const sessionQuery = useQuery<SessionGetResponse>({
    queryKey: ['session', id],
    queryFn: () => api.getSession(id),
    enabled: validId,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  // Local state of in-progress answers and current step
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [step, setStep] = useState<number>(1);
  const [showSummary, setShowSummary] = useState<boolean>(false);
  const [editingFromSummary, setEditingFromSummary] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [rewriteEditMode, setRewriteEditMode] = useState<boolean>(false);
  const [showStyle, setShowStyle] = useState<boolean>(false);
  const location = useLocation() as { state: { openRewrite?: boolean } | null };
  const [searchParams] = useSearchParams();
  const readonly = searchParams.get('readonly') === '1';
  // Read-only entry (e.g. from /history) suppresses the rewrite flow entirely:
  // no rewrite CTA, no auto-open, no incidental kredit spending on archive viewing.
  const initialOpenRewrite = !readonly && (location.state?.openRewrite ?? false);
  const initializedRef = useRef(false);
  const debounceRef = useRef<number | null>(null);

  // Initialize once after the query resolves
  useEffect(() => {
    if (initializedRef.current) return;
    if (!sessionQuery.data) return;
    initializedRef.current = true;
    // Own-text sessions keep their own form state (OwnTextSession).
    if (sessionQuery.data.idea.code === OWN_TEXT_IDEA_CODE) return;

    const data = sessionQuery.data;
    const { session, idea, stale } = data;
    if (stale) return;

    const flags = {
      niche: data.requires_niche,
      product: data.requires_product,
      target_audience: data.requires_target_audience,
    };
    const steps = buildSteps(idea.questions, flags);
    if (steps.length === 0) return;

    const serverAnswers = (session.answers ?? {}) as Record<string, string>;
    const cached = loadDraft(session.id);
    const localAnswers = cached ? filterAnswersInRange(cached.answers, idea.questions.length) : {};

    // Profile-step prefill: if the user has not yet supplied an answer for a
    // profile step (neither server-side nor local cache), seed it from the profile.
    const profileSeed: Record<string, string> = {};
    for (const s of steps) {
      if (s.kind !== 'profile') continue;
      if (serverAnswers[s.key] !== undefined) continue;
      if (localAnswers[s.key] !== undefined) continue;
      const v = data.profile[s.key];
      if (typeof v === 'string' && v.length > 0) profileSeed[s.key] = v;
    }

    const merged: Record<string, string> = {
      ...profileSeed,
      ...serverAnswers,
      ...localAnswers,
    };
    setAnswers(merged);

    // Find the first step whose answer is missing from the server (the cache is
    // ephemeral and shouldn't change "where to resume from").
    const firstUnanswered = steps.findIndex((s) => serverAnswers[s.key] === undefined);
    if (firstUnanswered === -1) {
      setShowSummary(true);
      setStep(steps.length);
    } else {
      setStep(firstUnanswered + 1);
      setShowSummary(false);
    }
  }, [sessionQuery.data]);

  // Debounced localStorage save on answers change
  useEffect(() => {
    if (!sessionQuery.data || sessionQuery.data.idea.code === OWN_TEXT_IDEA_CODE) return;
    if (debounceRef.current !== null) {
      window.clearTimeout(debounceRef.current);
    }
    debounceRef.current = window.setTimeout(() => {
      saveDraft(sessionQuery.data!.session.id, answers);
    }, SAVE_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current !== null) {
        window.clearTimeout(debounceRef.current);
      }
    };
  }, [answers, sessionQuery.data]);

  const saveMutation = useMutation({
    mutationFn: (next: Record<string, string>) => api.updateSessionAnswers(id, next),
    onSuccess: ({ session }) => {
      const fromServer = (session.answers ?? {}) as Record<string, string>;
      saveDraft(session.id, fromServer);
      qc.setQueryData<SessionGetResponse>(['session', id], (old) =>
        old ? { ...old, session } : old,
      );
      setSaveError(null);
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : 'Не удалось сохранить';
      setSaveError(msg);
    },
  });

  if (!validId) {
    return (
      <CenteredMessage
        title="Сессия не найдена"
        action={
          <Button onClick={() => navigate('/')} variant="primary" leftIcon={<Home size={18} />}>
            На главную
          </Button>
        }
      />
    );
  }

  if (sessionQuery.isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3">
        <Spinner />
        <p className="text-sm text-tg-hint">Загружаем сессию</p>
      </div>
    );
  }

  if (sessionQuery.error) {
    const err = sessionQuery.error;
    const notFound = err instanceof ApiError && err.status === 404;
    return (
      <CenteredMessage
        title={notFound ? 'Сессия не найдена' : 'Не удалось загрузить'}
        action={
          <div className="flex flex-col gap-2 w-full max-w-xs">
            {!notFound && (
              <Button
                onClick={() => sessionQuery.refetch()}
                variant="primary"
                leftIcon={<RotateCcw size={18} />}
              >
                Повторить
              </Button>
            )}
            <Button onClick={() => navigate('/')} variant="secondary" leftIcon={<Home size={18} />}>
              На главную
            </Button>
          </div>
        }
      />
    );
  }

  const { idea, session, stale } = sessionQuery.data!;

  // Iter 20: own-text carousel sessions have their own single-screen flow.
  if (idea.code === OWN_TEXT_IDEA_CODE) {
    return (
      <OwnTextSession
        key={session.id}
        data={sessionQuery.data!}
        readonly={readonly}
        initialOpenRewrite={initialOpenRewrite}
      />
    );
  }
  const formatCode = sessionQuery.data!.format_code;
  const isCarousel = formatCode === 'carousel';
  const questions = idea.questions;
  const flags = {
    niche: sessionQuery.data!.requires_niche,
    product: sessionQuery.data!.requires_product,
    target_audience: sessionQuery.data!.requires_target_audience,
  };
  const steps = buildSteps(questions, flags);
  const totalSteps = steps.length;

  if (stale) {
    return <StaleSession sessionId={session.id} ideaId={idea.id} />;
  }

  if (totalSteps === 0) {
    return <StaleSession sessionId={session.id} ideaId={idea.id} />;
  }

  // If session is already completed, render ResultView (handles "user closed TMA mid-generation" case).
  if (session.status === 'completed' && sessionQuery.data!.last_generation) {
    if (rewriteEditMode) {
      if (showSummary) {
        return (
          <SummaryView
            session={session}
            steps={steps}
            answers={answers}
            mode="rewrite-edit"
            onEdit={(n) => {
              setShowSummary(false);
              setStep(n);
              setEditingFromSummary(true);
            }}
            onRecreate={async () => {
              await api.deleteSession(session.id);
              clearDraft(session.id);
              const res = await api.createSession({ idea_id: idea.id });
              navigate(`/sessions/${res.session.id}`, { replace: true });
            }}
            onResumeRewrite={() => {
              setRewriteEditMode(false);
              navigate(`/sessions/${session.id}`, {
                replace: true,
                state: { openRewrite: true },
              });
            }}
            onCancelEdit={() => {
              setRewriteEditMode(false);
              setShowSummary(false);
            }}
          />
        );
      }
      // else: showSummary===false — fall through to Wizard render path below
    } else {
      const resultProps = {
        sessionId: session.id,
        rewriteCost: sessionQuery.data!.rewrite_cost,
        initialOpenRewrite,
        readonly,
        onEditAnswers: () => {
          setRewriteEditMode(true);
          setShowSummary(true);
        },
      };
      return isCarousel ? <CarouselResultView {...resultProps} /> : <ResultView {...resultProps} />;
    }
  }
  if (session.status === 'failed') {
    return (
      <FailedView
        sessionId={session.id}
        onRetry={() => {
          // Reopen as a fresh session with the same idea
          api.deleteSession(session.id).catch(() => {});
          navigate(`/sessions/new?idea_id=${idea.id}`, { replace: true });
        }}
      />
    );
  }

  if (isCarousel && showStyle) {
    return (
      <StyleStep
        sessionId={session.id}
        initialStyle={sessionQuery.data!.style_code}
        initialNick={sessionQuery.data!.nick}
        onBack={() => setShowStyle(false)}
        onGenerated={() => setShowStyle(false)}
      />
    );
  }

  // Final summary screen
  if (showSummary) {
    return (
      <SummaryView
        session={session}
        steps={steps}
        answers={answers}
        formatCode={formatCode}
        onChooseStyle={() => setShowStyle(true)}
        onEdit={(n) => {
          setShowSummary(false);
          setStep(n);
          setEditingFromSummary(true);
        }}
        onRecreate={async () => {
          await api.deleteSession(session.id);
          clearDraft(session.id);
          const res = await api.createSession({ idea_id: idea.id });
          navigate(`/sessions/${res.session.id}`, { replace: true });
        }}
      />
    );
  }

  // Wizard
  const currentStep = steps[step - 1];
  if (!currentStep) {
    // step out of range — defer to next tick to avoid setState-during-render
    queueMicrotask(() => setStep(totalSteps));
    return null;
  }
  const currentKey = currentStep.key;
  const currentValue = answers[currentKey] ?? '';

  function setCurrentValue(next: string) {
    setAnswers((prev) => ({ ...prev, [currentKey]: next }));
  }

  function handleBack() {
    setSaveError(null);
    if (editingFromSummary) {
      setEditingFromSummary(false);
      setShowSummary(true);
      return;
    }
    if (step === 1) {
      // Откатываемся по истории, а не пушим карточку поверх опросника: иначе
      // «Назад» на карточке вернул бы пользователя обратно в вопросы.
      if (canGoBack()) navigate(-1);
      else navigate(`/ideas/${idea.id}`, { replace: true });
      return;
    }
    setStep(step - 1);
  }

  function handleNext() {
    setSaveError(null);
    const trimmed = currentValue.trim();
    const nextAnswers = { ...answers, [currentKey]: trimmed };
    const payload = buildPayloadFromAnswers(nextAnswers, steps);

    saveMutation.mutate(payload, {
      onSuccess: () => {
        setAnswers(nextAnswers);
        if (editingFromSummary) {
          setEditingFromSummary(false);
          setShowSummary(true);
        } else if (step === totalSteps) {
          setShowSummary(true);
        } else {
          setStep(step + 1);
        }
      },
    });
  }

  return (
    <QuestionStep
      step={step}
      total={totalSteps}
      question={currentStep.question}
      value={currentValue}
      onChange={setCurrentValue}
      onNext={handleNext}
      onBack={handleBack}
      saving={saveMutation.isPending}
      saveError={saveError}
      nextLabel={editingFromSummary ? 'Сохранить' : undefined}
      onExit={() => navigate('/')}
      sessionId={session.id}
    />
  );
}

interface SummaryViewProps {
  session: SessionDetailDto;
  steps: Step[];
  answers: Record<string, string>;
  onEdit: (step: number) => void;
  onRecreate: () => Promise<void> | void;
  mode?: 'initial' | 'rewrite-edit';
  onResumeRewrite?: () => void;
  onCancelEdit?: () => void;
  formatCode?: string;
  onChooseStyle?: () => void;
}

function SummaryView({
  session,
  steps,
  answers,
  onEdit,
  onRecreate,
  mode = 'initial',
  onResumeRewrite,
  onCancelEdit,
  formatCode,
  onChooseStyle,
}: SummaryViewProps) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [generationResult, setGenerationResult] = useState<GenerationDto | null>(null);

  const isRewriteEdit = mode === 'rewrite-edit';

  const generateMutation = useMutation({
    mutationFn: () => api.generateSession(session.id),
    onSuccess: (data) => {
      haptic('success');
      notifyTurboCap(qc, data.charged_via);
      setGenerationResult(data.generation);
      qc.invalidateQueries({ queryKey: ['profile'] });
      qc.setQueryData<SessionGetResponse>(['session', session.id], (old) =>
        old
          ? {
              ...old,
              session: { ...old.session, status: 'completed', free_rewrites_used: 0 },
              last_generation: data.generation,
              rewrite_cost: 'free' as const,
            }
          : old,
      );
    },
    onError: () => {
      haptic('error');
      // Re-fetch: the session may have flipped to `failed` server-side even
      // though this mutation errored, so show the real state instead of
      // letting a retry race a stale `answering`/`completed` snapshot.
      qc.invalidateQueries({ queryKey: ['session', session.id] });
    },
  });

  if (generateMutation.isPending) {
    return <GeneratingView />;
  }
  if (generationResult) {
    return (
      <ResultView
        sessionId={session.id}
        rewriteCost="free"
        initialOpenRewrite={false}
        onEditAnswers={() => {
          /* not reachable from initial flow */
        }}
      />
    );
  }

  async function handleRecreate() {
    setBusy(true);
    try {
      await onRecreate();
    } finally {
      setBusy(false);
    }
  }

  const error = generateMutation.error;
  const isPaywall = error instanceof ApiError && error.status === 402;
  const errorMessage =
    error instanceof Error
      ? isPaywall
        ? 'Не хватает кредитов. Пополните баланс, чтобы продолжить.'
        : `Не удалось сгенерировать: ${error.message}`
      : null;

  return (
    <div className="min-h-screen p-4 max-w-lg mx-auto pb-8 safe-top safe-bottom">
      <header className="mb-4">
        <h1 className="text-xl font-bold mb-1">
          {isRewriteEdit ? 'Поправьте ответы' : 'Все ответы получены'}
        </h1>
        <p className="text-sm text-tg-hint leading-snug">
          {isRewriteEdit
            ? 'Измените любые ответы и нажмите «Сгенерировать заново».'
            : formatCode === 'carousel'
              ? 'Дальше выберем стиль слайдов и соберём карусель. Можете проверить ответы и поправить любой.'
              : 'Сейчас сгенерируем сценарий. Можете проверить ответы и поправить любой.'}
        </p>
      </header>

      <ol className="space-y-2 mb-6">
        {steps.map((s, idx) => {
          const stepNum = idx + 1;
          const answer = answers[s.key] ?? '';
          return (
            <li key={s.key}>
              <Card variant="surface" padding="sm">
                <div className="flex justify-between items-start gap-2 mb-1">
                  <p className="text-xs font-semibold uppercase tracking-wider text-tg-hint">
                    Q{stepNum}
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      haptic('light');
                      onEdit(stepNum);
                    }}
                    className="inline-flex items-center gap-1 text-xs text-brand font-medium hover:opacity-80 transition-opacity"
                  >
                    <Pencil size={12} />
                    Изменить
                  </button>
                </div>
                <p className="text-sm font-medium mb-1.5">{s.question.title}</p>
                {s.question.description && (
                  <p className="text-xs text-tg-hint mb-1.5 whitespace-pre-wrap">
                    {s.question.description}
                  </p>
                )}
                <p className="text-sm whitespace-pre-wrap text-tg-text/80">{answer}</p>
              </Card>
            </li>
          );
        })}
      </ol>

      {errorMessage && (
        <Toast tone={isPaywall ? 'warning' : 'destructive'} className="mb-3">
          {errorMessage}
        </Toast>
      )}

      {isRewriteEdit ? (
        <>
          <Button
            onClick={onResumeRewrite}
            disabled={busy}
            variant="primary"
            size="lg"
            fullWidth
            leftIcon={<Sparkles size={18} />}
            className="mb-2"
          >
            Сгенерировать заново
          </Button>
          <Button onClick={onCancelEdit} disabled={busy} variant="secondary" size="lg" fullWidth>
            Отмена
          </Button>
        </>
      ) : (
        <>
          {formatCode === 'carousel' ? (
            <Button
              onClick={onChooseStyle}
              disabled={busy}
              variant="primary"
              size="lg"
              fullWidth
              leftIcon={<Sparkles size={18} />}
              className="mb-2"
            >
              Выбрать стиль
            </Button>
          ) : (
            <Button
              onClick={() => generateMutation.mutate()}
              disabled={generateMutation.isPending || busy}
              loading={generateMutation.isPending}
              variant="primary"
              size="lg"
              fullWidth
              leftIcon={!generateMutation.isPending ? <Sparkles size={18} /> : undefined}
              className="mb-2"
            >
              {error ? 'Повторить' : 'Сгенерировать сценарий'}
            </Button>
          )}

          <Button
            onClick={() => navigate('/')}
            variant="secondary"
            size="lg"
            fullWidth
            leftIcon={<Home size={18} />}
            className="mb-2"
            hapticKind="light"
          >
            На главную
          </Button>

          <Button
            onClick={handleRecreate}
            disabled={busy || generateMutation.isPending}
            variant="ghost"
            size="sm"
            fullWidth
            leftIcon={<Trash2 size={14} />}
            className="text-tg-hint"
            hapticKind="warning"
          >
            {busy ? 'Создаём...' : 'Удалить и начать заново'}
          </Button>
        </>
      )}

      <p className="text-xs text-tg-hint/70 text-center mt-4 tabular-nums">Сессия #{session.id}</p>
    </div>
  );
}

function GeneratingView() {
  return (
    <div className="min-h-screen p-6 max-w-lg mx-auto flex flex-col items-center justify-center text-center safe-top safe-bottom">
      <Spinner size="lg" className="mb-4" />
      <h1 className="text-lg font-semibold mb-1">Генерируем сценарий</h1>
      <p className="text-sm text-tg-hint">Обычно занимает 15-20 секунд.</p>
    </div>
  );
}

interface ResultViewProps {
  sessionId: number;
  rewriteCost: 'free' | 'paid' | 'blocked';
  initialOpenRewrite: boolean;
  readonly?: boolean;
  onEditAnswers: () => void;
}

function ResultView({
  sessionId,
  rewriteCost,
  initialOpenRewrite,
  readonly = false,
  onEditAnswers,
}: ResultViewProps) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [copied, setCopied] = useState(false);
  const [rewriteOpen, setRewriteOpen] = useState(initialOpenRewrite);

  useEffect(() => {
    if (initialOpenRewrite) setRewriteOpen(true);
  }, [initialOpenRewrite]);

  const sessionData = qc.getQueryData<SessionGetResponse>(['session', sessionId]);
  const generation = sessionData?.last_generation ?? null;

  const profileQuery = useQuery({
    queryKey: ['profile'],
    queryFn: () => api.getProfile(),
    refetchOnWindowFocus: false,
  });
  const balance = profileQuery.data?.user.balanceCredits ?? null;
  const isAdmin = profileQuery.data?.user.isAdmin ?? false;
  const pricing = usePricing();
  const turboUntil = profileQuery.data?.user.turboUntil ?? null;

  if (!generation) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3">
        <Spinner />
        <p className="text-sm text-tg-hint">Загружаем сценарий</p>
      </div>
    );
  }

  async function handleCopy() {
    if (!generation) return;
    try {
      await navigator.clipboard.writeText(generation.content);
      haptic('success');
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // best-effort; ignore
    }
  }

  function handleRewriteSuccess(resp: SessionRewriteResponse) {
    notifyTurboCap(qc, resp.charged_via);
    clearRewriteFeedback(sessionId);
    qc.setQueryData<SessionGetResponse>(['session', sessionId], (old) => {
      if (!old) return old;
      return {
        ...old,
        session: {
          ...old.session,
          status: 'completed',
          free_rewrites_used: resp.free_rewrites_used,
        },
        last_generation: resp.generation,
        rewrite_cost: resp.rewrite_cost,
      };
    });
    qc.invalidateQueries({ queryKey: ['profile'] });
    setRewriteOpen(false);
  }

  function handleEditAnswersClick(currentFeedback: string) {
    saveRewriteFeedback(sessionId, currentFeedback);
    setRewriteOpen(false);
    onEditAnswers();
  }

  const dt = new Date(generation.created_at);
  const dateStr = dt.toLocaleString('ru-RU', { dateStyle: 'long', timeStyle: 'short' });

  const rewriteLabel =
    rewriteCost === 'free'
      ? 'Переписать (бесплатно)'
      : rewriteCost === 'paid'
        ? paidRewriteLabel(pricing.rewrite, turboUntil)
        : 'Переписать (нужны кредиты)';

  return (
    <div className="min-h-screen p-4 max-w-lg mx-auto pb-8 safe-top safe-bottom">
      {readonly && (
        <Link
          to="/history"
          className="inline-flex items-center gap-1 text-sm text-tg-hint hover:text-tg-text transition-colors mb-2"
        >
          <ArrowLeft size={16} />
          Назад
        </Link>
      )}
      <header className="mb-3">
        <h1 className="text-xl font-bold mb-1">Ваш сценарий</h1>
        <p className="text-xs text-tg-hint">{dateStr}</p>
      </header>

      <Card variant="elevated" padding="md" className="mb-4">
        <Markdown>{generation.content}</Markdown>
      </Card>

      {!readonly && (
        <>
          <Button
            onClick={() => setRewriteOpen(true)}
            disabled={rewriteCost === 'blocked'}
            variant="primary"
            size="lg"
            fullWidth
            leftIcon={<RotateCcw size={18} />}
            className="mb-2"
          >
            {rewriteLabel}
          </Button>

          {rewriteCost === 'blocked' && (
            <Button
              onClick={() => navigate('/balance')}
              variant="secondary"
              size="lg"
              fullWidth
              leftIcon={<Wallet size={18} />}
              className="mb-2"
            >
              Пополнить баланс
            </Button>
          )}
        </>
      )}

      <div className="flex gap-2 mb-3">
        <Button
          onClick={handleCopy}
          variant="secondary"
          size="md"
          fullWidth
          leftIcon={copied ? <Check size={16} /> : <Copy size={16} />}
          hapticKind={false}
        >
          {copied ? 'Скопировано' : 'Скопировать'}
        </Button>
        <Button
          onClick={() => navigate('/')}
          variant="secondary"
          size="md"
          fullWidth
          leftIcon={<Home size={16} />}
          hapticKind="light"
        >
          На главную
        </Button>
      </div>

      {!readonly && (
        <p className="text-xs text-tg-hint text-center">
          {[
            generation.is_free_rewrite ? 'Бесплатная перегенерация.' : null,
            balance !== null ? `Баланс: ${balance}` : null,
          ]
            .filter(Boolean)
            .join(' ')}
        </p>
      )}

      {isAdmin && (
        <p className="text-xs text-tg-hint/70 text-center mt-2 tabular-nums">
          {generation.model ?? '—'} · {generation.tokens_used ?? '—'} t · session #{sessionId}
        </p>
      )}

      {rewriteOpen && !readonly && (
        <RewriteModal
          sessionId={sessionId}
          rewriteCost={rewriteCost}
          initialFeedback={loadRewriteFeedback(sessionId)}
          onSubmitted={handleRewriteSuccess}
          onClose={() => setRewriteOpen(false)}
          onEditAnswers={handleEditAnswersClick}
        />
      )}
    </div>
  );
}

interface FailedViewProps {
  sessionId: number;
  onRetry: () => void;
}

function FailedView({ sessionId, onRetry }: FailedViewProps) {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen p-6 max-w-lg mx-auto flex flex-col items-center justify-center text-center safe-top safe-bottom">
      <div className="w-16 h-16 rounded-2xl bg-destructive-subtle text-destructive flex items-center justify-center mb-4">
        <AlertCircle size={28} />
      </div>
      <h1 className="text-lg font-semibold mb-2">Не удалось сгенерировать</h1>
      <p className="text-sm text-tg-hint mb-6 max-w-xs">
        Что-то пошло не так на стороне сервиса. Кредит возвращён на баланс.
      </p>
      <Button
        onClick={onRetry}
        variant="primary"
        size="lg"
        leftIcon={<RotateCcw size={18} />}
        className="w-full max-w-xs mb-2"
      >
        Попробовать снова
      </Button>
      <Button
        onClick={() => navigate('/')}
        variant="secondary"
        size="lg"
        leftIcon={<Home size={18} />}
        className="w-full max-w-xs"
        hapticKind="light"
      >
        На главную
      </Button>
      <p className="text-xs text-tg-hint/70 mt-4 tabular-nums">Сессия #{sessionId}</p>
    </div>
  );
}

function CenteredMessage({ title, action }: { title: string; action: React.ReactNode }) {
  return (
    <div className="min-h-screen p-6 max-w-lg mx-auto flex flex-col items-center justify-center text-center safe-top safe-bottom">
      <h1 className="text-lg font-semibold mb-4">{title}</h1>
      {action}
    </div>
  );
}
